import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ViewStyle,
} from 'react-native';
import { RolesByType } from '../DLOFilterByRoleModal';

export interface ScheduleRowData {
  id: string;
  roleName: string;
  sunday: { fcst: number; schd: number };
  monday: { fcst: number; schd: number };
  tuesday: { fcst: number; schd: number };
  wednesday: { fcst: number; schd: number };
  thursday: { fcst: number; schd: number };
  friday: { fcst: number; schd: number };
  saturday: { fcst: number; schd: number };
  total: { fcst: number; schd: number };
  variance: number;
}

export interface DLOCreateScheduleTableContentProps {
  data?: ScheduleRowData[];
  style?: ViewStyle;
  filteredRoles?: RolesByType;
}

// Default data from Figma design
const defaultData: ScheduleRowData[] = [
  {
    id: '1',
    roleName: 'PICKER',
    sunday: { fcst: 0, schd: 0 },
    monday: { fcst: 0, schd: 0 },
    tuesday: { fcst: 0, schd: 0 },
    wednesday: { fcst: 0, schd: 0 },
    thursday: { fcst: 0, schd: 0 },
    friday: { fcst: 0, schd: 0 },
    saturday: { fcst: 0, schd: 0 },
    total: { fcst: 0, schd: 0 },
    variance: 0,
  },
  {
    id: '2',
    roleName: 'Totals (All Roles)',
    sunday: { fcst: 0, schd: 0 },
    monday: { fcst: 0, schd: 0 },
    tuesday: { fcst: 0, schd: 0 },
    wednesday: { fcst: 0, schd: 0 },
    thursday: { fcst: 0, schd: 0 },
    friday: { fcst: 0, schd: 0 },
    saturday: { fcst: 0, schd: 0 },
    total: { fcst: 0, schd: 0 },
    variance: 0,
  },
];

const DLOCreateScheduleTableContent: React.FC<DLOCreateScheduleTableContentProps> = ({
  data = defaultData,
  style,
  filteredRoles,
}) => {
  // Helper function to format numbers to 2 decimal places
  const formatToTwoDecimals = (value: number): string => {
    return value.toFixed(2);
  };

  // Function to get color for scheduled hours based on forecast comparison
  // Modified: Accept roleName to double fcst for LAYER/LAYER SUPPORT
  const getSchdHoursColor = (fcstHours: number, schdHours: number, roleName?: string): string => {
    let effectiveFcst = fcstHours;
    
    console.log(`Calculating color for role: ${roleName}, fcst: ${effectiveFcst}, schd: ${schdHours}`);
    // If forecast is 0, don't apply color logic
    if (effectiveFcst === 0 && schdHours===0) {
      return '#080707ff'; // Default black color
    }

    // Calculate 10% bounds
    const lowerBound = effectiveFcst - (effectiveFcst * 0.10);
    const upperBound = effectiveFcst + (effectiveFcst * 0.10);

    // Determine color based on range
    if (schdHours >= lowerBound && schdHours <= upperBound) {
      return '#008553'; // Green - within 10% range
    } else if (schdHours < lowerBound) {
      return '#3A3A3A'; // Black - below range
    } else {
      return '#DC2626'; // Red - above range
    }
  };

  // Function to simplify role name by removing role type suffixes/prefixes
  const getSimplifiedRoleName = (roleName: string): string => {
    // If it's "Totals (All Roles)", keep it as "Totals"
    if (roleName.includes('Totals')) {
      return 'Totals (All Roles)';
    }
    
    // For other roles, remove common role type patterns
    let simplified = roleName
      .replace(/\s*\(.*\)$/, '') // Remove anything in parentheses at the end
      .replace(/\s*-\s*\w+$/, '') // Remove dash followed by role type at the end
      .replace(/^\w+\s*-\s*/, '') // Remove role type prefix with dash
      .trim();
    
    return simplified || roleName; // Return original if simplified is empty
  };


  // Function to check if a role is enabled based on filtered roles
  const isRoleEnabled = (roleName: string): boolean => {
    if (!filteredRoles) return true; // If no filter is set, show all roles
    
    // Always show "Totals" rows
    if (roleName.includes('Totals')) {
      return true;
    }
    
    // Check all role categories to see if this role is enabled
    const allRoles = [
      ...filteredRoles.DSD,
      ...filteredRoles['NON-DSD'],
      ...filteredRoles.Retail,
      ...filteredRoles.Other,
    ];
    
    // Find matching role by name (case insensitive, simplified comparison)
    const matchingRole = allRoles.find(role => {
      const simplifiedRoleName = getSimplifiedRoleName(roleName).toLowerCase();
      const simplifiedFilterName = role.name.toLowerCase();
      return simplifiedRoleName.includes(simplifiedFilterName) || 
             simplifiedFilterName.includes(simplifiedRoleName);
    });
    
    return matchingRole ? matchingRole.enabled : true; // Default to enabled if not found
  };


  const prcessedData = data.map(item => {
    const simplifiedRole = getSimplifiedRoleName(item.roleName).trim().toUpperCase();
    const isLayerRole = simplifiedRole === 'LAYER SUPPORT' || simplifiedRole === 'LAYER';

    // Only double the fcst values for LAYER/LAYER SUPPORT, do not mutate original item
    const doubleFcst = (fcst: number) => isLayerRole ? fcst * 2 : fcst;
   

    return {
      ...item,
      sunday: { fcst: doubleFcst(item.sunday.fcst), schd: item.sunday.schd },
      monday: { fcst: doubleFcst(item.monday.fcst), schd: item.monday.schd },
      tuesday: { fcst: doubleFcst(item.tuesday.fcst), schd: item.tuesday.schd },
      wednesday: { fcst: doubleFcst(item.wednesday.fcst), schd: item.wednesday.schd },
      thursday: { fcst: doubleFcst(item.thursday.fcst), schd: item.thursday.schd },
      friday: { fcst: doubleFcst(item.friday.fcst), schd: item.friday.schd },
      saturday: { fcst: doubleFcst(item.saturday.fcst), schd: item.saturday.schd },
      total: { fcst: doubleFcst(item.total.fcst), schd: item.total.schd },
      variance:  doubleFcst(item.total.fcst)- item.total.schd
    };
  });
  // Filter data based on enabled roles
  // Show roles that are either:
  // 1. Manually enabled by user (regardless of variance)
  // 2. Are totals rows (always shown)
  const filteredData = prcessedData.filter(item => {
    const isEnabled = isRoleEnabled(item.roleName);
    
    // Always show totals rows regardless of variance or filter state  
    const isTotalsRow = item.roleName.toLowerCase().includes('totals');
    if (isTotalsRow) {
      return true;
    }
    
    // Show role only if user has enabled it (either manually or by default)
    // This respects the user's toggle state:
    // - Roles with zero variance are disabled by default but can be manually enabled
    // - Roles with non-zero variance are enabled by default but can be manually disabled
    return isEnabled;
  });

  // Function to calculate totals from filtered data (excluding existing totals rows)
  const calculateTotalsFromFilteredData = (filteredData: ScheduleRowData[]): ScheduleRowData => {
    // Filter out any existing totals rows to avoid double counting
    const dataWithoutTotals = filteredData.filter(item => !item.roleName.toLowerCase().includes('totals'));
    
    // Initialize totals
    const totals = {
      sunday: { fcst: 0, schd: 0 },
      monday: { fcst: 0, schd: 0 },
      tuesday: { fcst: 0, schd: 0 },
      wednesday: { fcst: 0, schd: 0 },
      thursday: { fcst: 0, schd: 0 },
      friday: { fcst: 0, schd: 0 },
      saturday: { fcst: 0, schd: 0 },
      total: { fcst: 0, schd: 0 },
    };

    // Sum up all values from all roles (applying LAYER doubling to match display)
    dataWithoutTotals.forEach(item => {
      const simplifiedRole = getSimplifiedRoleName(item.roleName).trim().toUpperCase();
      const isLayerRole = simplifiedRole === 'LAYER SUPPORT' || simplifiedRole === 'LAYER';
      
      // Apply doubling factor for LAYER roles to match the display logic
      const fcstFactor = isLayerRole ? 1 : 1;
      const schdFactor = isLayerRole ? 1 : 1;
      
      totals.sunday.fcst += item.sunday.fcst * fcstFactor;
      totals.sunday.schd += item.sunday.schd * schdFactor;
      totals.monday.fcst += item.monday.fcst * fcstFactor;
      totals.monday.schd += item.monday.schd * schdFactor;
      totals.tuesday.fcst += item.tuesday.fcst * fcstFactor;
      totals.tuesday.schd += item.tuesday.schd * schdFactor;
      totals.wednesday.fcst += item.wednesday.fcst * fcstFactor;
      totals.wednesday.schd += item.wednesday.schd * schdFactor;
      totals.thursday.fcst += item.thursday.fcst * fcstFactor;
      totals.thursday.schd += item.thursday.schd * schdFactor;
      totals.friday.fcst += item.friday.fcst * fcstFactor;
      totals.friday.schd += item.friday.schd * schdFactor;
      totals.saturday.fcst += item.saturday.fcst * fcstFactor;
      totals.saturday.schd += item.saturday.schd * schdFactor;
      totals.total.fcst += item.total.fcst * fcstFactor;
      totals.total.schd += item.total.schd * schdFactor;
    });

    // Calculate variance as difference between total fcst and total schd
    const variance = totals.total.fcst - totals.total.schd;
    console.log("Calculated Totals Variance:", variance, totals);
    return {
      id: 'calculated-totals',
      roleName: 'Totals (All Roles)',
      sunday: { 
        fcst: parseFloat(totals.sunday.fcst.toFixed(2)), 
        schd: parseFloat(totals.sunday.schd.toFixed(2)) 
      },
      monday: { 
        fcst: parseFloat(totals.monday.fcst.toFixed(2)), 
        schd: parseFloat(totals.monday.schd.toFixed(2)) 
      },
      tuesday: { 
        fcst: parseFloat(totals.tuesday.fcst.toFixed(2)), 
        schd: parseFloat(totals.tuesday.schd.toFixed(2)) 
      },
      wednesday: { 
        fcst: parseFloat(totals.wednesday.fcst.toFixed(2)), 
        schd: parseFloat(totals.wednesday.schd.toFixed(2)) 
      },
      thursday: { 
        fcst: parseFloat(totals.thursday.fcst.toFixed(2)), 
        schd: parseFloat(totals.thursday.schd.toFixed(2)) 
      },
      friday: { 
        fcst: parseFloat(totals.friday.fcst.toFixed(2)), 
        schd: parseFloat(totals.friday.schd.toFixed(2)) 
      },
      saturday: { 
        fcst: parseFloat(totals.saturday.fcst.toFixed(2)), 
        schd: parseFloat(totals.saturday.schd.toFixed(2)) 
      },
      total: { 
        fcst: parseFloat(totals.total.fcst.toFixed(2)), 
        schd: parseFloat(totals.total.schd.toFixed(2)) 
      },
      variance: parseFloat(variance.toFixed(2)),
    };
  };

  // Remove any existing totals rows from filtered data and add calculated totals
  const dataWithoutExistingTotals = filteredData.filter(item => !item.roleName.toLowerCase().includes('totals'));
  const calculatedTotals = calculateTotalsFromFilteredData(filteredData);
  const finalData = [...dataWithoutExistingTotals, calculatedTotals];

  const renderDataRow = (item: ScheduleRowData) => {
    const isTotalsRow = item.roleName.toLowerCase().includes('totals');
    // Check if the role is LAYER SUPPORT or LAYER (case-insensitive)
    const simplifiedRole = getSimplifiedRoleName(item.roleName).trim().toUpperCase();
    const isLayerSupport = simplifiedRole === 'LAYER SUPPORT' || simplifiedRole === 'LAYER';

    // Helper to get possibly doubled fcst value for LAYER SUPPORT/LAYER
    const getDayFcst = (fcst: number) => isLayerSupport ? fcst * 1 : fcst;

    return (
      <View key={item.id} style={[styles.dataRow, isTotalsRow && styles.totalsRow]}>
        {/* Left section - Role name */}
        <View style={styles.leftSection}>
          <Text style={[styles.roleNameText, isTotalsRow && styles.totalsRoleNameText]}>
            {getSimplifiedRoleName(item.roleName)}
          </Text>
        </View>

        {/* Major divider between ROLES and days */}
        <View style={styles.majorDivider} />

        {/* Data section */}
        <View style={styles.dataSection}>
          {/* Sunday */}
          <View style={styles.dataCellContainer}>
            <View style={styles.dataCell}>
              <Text style={[styles.dataText, isTotalsRow && styles.totalsDataText]}>{formatToTwoDecimals(getDayFcst(item.sunday.fcst))}</Text>
            </View>
            <View style={styles.cellDivider} />
            <View style={styles.dataCell}>
                <Text style={[
                styles.dataText, 
                isTotalsRow && styles.totalsDataText,
                { color: getSchdHoursColor(item.sunday.fcst, item.sunday.schd, item.roleName) }
                ]}>
                {formatToTwoDecimals(item.sunday.schd)}
                </Text>
            </View>
          </View>
          <View style={styles.majorDivider} />

          {/* Monday */}
          <View style={styles.dataCellContainer}>
            <View style={styles.dataCell}>
              <Text style={[styles.dataText, isTotalsRow && styles.totalsDataText]}>{formatToTwoDecimals(getDayFcst(item.monday.fcst))}</Text>
            </View>
            <View style={styles.cellDivider} />
            <View style={styles.dataCell}>
              <Text style={[
                styles.dataText, 
                isTotalsRow && styles.totalsDataText,
                { color: getSchdHoursColor(item.monday.fcst, item.monday.schd, item.roleName) }
              ]}>
                {formatToTwoDecimals(item.monday.schd)}
              </Text>
            </View>
          </View>
          <View style={styles.majorDivider} />

          {/* Tuesday */}
          <View style={styles.dataCellContainer}>
            <View style={styles.dataCell}>
              <Text style={[styles.dataText, isTotalsRow && styles.totalsDataText]}>{formatToTwoDecimals(getDayFcst(item.tuesday.fcst))}</Text>
            </View>
            <View style={styles.cellDivider} />
            <View style={styles.dataCell}>
              <Text style={[
                styles.dataText, 
                isTotalsRow && styles.totalsDataText,
                { color: getSchdHoursColor(item.tuesday.fcst, item.tuesday.schd, item.roleName) }
              ]}>
                {formatToTwoDecimals(item.tuesday.schd)}
              </Text>
            </View>
          </View>
          <View style={styles.majorDivider} />

          {/* Wednesday */}
          <View style={styles.dataCellContainer}>
            <View style={styles.dataCell}>
              <Text style={[styles.dataText, isTotalsRow && styles.totalsDataText]}>{formatToTwoDecimals(getDayFcst(item.wednesday.fcst))}</Text>
            </View>
            <View style={styles.cellDivider} />
            <View style={styles.dataCell}>
              <Text style={[
                styles.dataText, 
                isTotalsRow && styles.totalsDataText,
                { color: getSchdHoursColor(item.wednesday.fcst, item.wednesday.schd, item.roleName) }
              ]}>
                {formatToTwoDecimals(item.wednesday.schd)}
              </Text>
            </View>
          </View>
          <View style={styles.majorDivider} />

          {/* Thursday */}
          <View style={styles.dataCellContainer}>
            <View style={styles.dataCell}>
              <Text style={[styles.dataText, isTotalsRow && styles.totalsDataText]}>{formatToTwoDecimals(getDayFcst(item.thursday.fcst))}</Text>
            </View>
            <View style={styles.cellDivider} />
            <View style={styles.dataCell}>
              <Text style={[
                styles.dataText, 
                isTotalsRow && styles.totalsDataText,
                { color: getSchdHoursColor(item.thursday.fcst, item.thursday.schd, item.roleName) }
              ]}>
                {formatToTwoDecimals(item.thursday.schd)}
              </Text>
            </View>
          </View>
          <View style={styles.majorDivider} />

          {/* Friday */}
          <View style={styles.dataCellContainer}>
            <View style={styles.dataCell}>
              <Text style={[styles.dataText, isTotalsRow && styles.totalsDataText]}>{formatToTwoDecimals(getDayFcst(item.friday.fcst))}</Text>
            </View>
            <View style={styles.cellDivider} />
            <View style={styles.dataCell}>
              <Text style={[
                styles.dataText, 
                isTotalsRow && styles.totalsDataText,
                { color: getSchdHoursColor(item.friday.fcst, item.friday.schd, item.roleName) }
              ]}>
                {formatToTwoDecimals(item.friday.schd)}
              </Text>
            </View>
          </View>
          <View style={styles.majorDivider} />

          {/* Saturday */}
          <View style={styles.dataCellContainer}>
            <View style={styles.dataCell}>
              <Text style={[styles.dataText, isTotalsRow && styles.totalsDataText]}>{formatToTwoDecimals(getDayFcst(item.saturday.fcst))}</Text>
            </View>
            <View style={styles.cellDivider} />
            <View style={styles.dataCell}>
              <Text style={[
                styles.dataText, 
                isTotalsRow && styles.totalsDataText,
                { color: getSchdHoursColor(item.saturday.fcst, item.saturday.schd, item.roleName) }
              ]}>
                {formatToTwoDecimals(item.saturday.schd)}
              </Text>
            </View>
          </View>
          <View style={styles.majorDivider} />

          {/* Total */}
          <View style={styles.dataCellContainer}>
            <View style={styles.dataCell}>
              <Text style={[styles.dataText, isTotalsRow && styles.totalsDataText]}>{formatToTwoDecimals(getDayFcst(item.total.fcst))}</Text>
            </View>
            <View style={styles.cellDivider} />
            <View style={styles.dataCell}>
              <Text style={[
                styles.dataText, 
                isTotalsRow && styles.totalsDataText,
                { color: getSchdHoursColor(item.total.fcst, item.total.schd, item.roleName) }
              ]}>
                {formatToTwoDecimals(item.total.schd)}
              </Text>
            </View>
          </View>
        </View>

        {/* Major divider between days and variance */}
        <View style={styles.majorDivider} />

        {/* Variance section */}
        <View style={styles.varianceSection}>
          <Text style={[
            styles.varianceText, 
            isTotalsRow && styles.totalsVarianceText,
            item.variance > 0 && styles.positiveVariance,
            item.variance < 0 && styles.negativeVariance
          ]}>
            {formatToTwoDecimals(Math.abs(item.variance))}
          </Text>
        </View>
      </View>
    );
  };

  return (
    <View style={[styles.container, style]}>
      {finalData.map(renderDataRow)}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 1416,
    backgroundColor: '#FFFFFF',
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 0,
    borderLeftColor: '#DFDFDF',
    borderRightColor: '#DFDFDF',
    borderBottomColor: '#DFDFDF',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  dataRow: {
    width: 1416,
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#DFDFDF',
    justifyContent: 'center',
  },
  leftSection: {
    width: 227, // Match header left section width
    height: 56,
    justifyContent: 'center',
    alignItems: 'center', // Center align like header
  },
  roleNameText: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    textAlign: 'center', // Center align to match header ROLES title
    marginLeft: -1, // Align with major divider
  },
  dataSection: {
    width: 1067, // Adjust to match header days section (after accounting for major dividers)
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
  },
  dataCellContainer: {
    width: 132, // Matches header day column width
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
  },
  dataCell: {
    width: 54,
    height: 56,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dataText: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    textAlign: 'center',
  },
  cellDivider: {
    width: 1,
    height: 56,
    backgroundColor: '#DFDFDF',
  },
  varianceSection: {
    width: 121, // Matches header variance column width
    height: 56,
    justifyContent: 'center',
    alignItems: 'center',
  },
  varianceText: {
    color: '#008553', // Green color for variance as shown in Figma
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    textAlign: 'center',
  },
  majorDivider: {
    width: 1,
    height: 56,
    backgroundColor: '#DFDFDF',
  },
  // Totals row specific styles
  totalsRow: {
    backgroundColor: '#F8F9FA', // Light background for totals row
  },
  totalsRoleNameText: {
    //fontWeight: '500', // Bold text for totals
    color: '#000000', // Darker text for emphasis
  },
  totalsDataText: {
    //fontWeight: '700', // Bold text for totals data
    color: '#000000', // Darker text for emphasis
  },
  totalsVarianceText: {
    //fontWeight: '700', // Bold text for totals variance
    color: '#008553', // Keep the green color but make it bold
  },
  // Conditional variance colors
  positiveVariance: {
    color: '#008553', // Green for positive variance
  },
  negativeVariance: {
    color: '#D32F2F', // Red for negative variance
  },
});

export default DLOCreateScheduleTableContent;
